import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Fab,
    CircularProgress,
    Grow,
    useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { keyframes } from '@emotion/react';
import AppDrawer from './AppDrawer/AppDrawer';
import { AuthContext } from '../context/authContext';
import frugalServicesClient from '../utils/frugal-services-client';
import { UserContext } from '../context/userContext';

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const MealWheel = () => {
    const theme = useTheme();
    const { user } = useContext(UserContext);
    const { setPasswordExpired } = useContext(AuthContext);
    const [meals, setMeals] = useState([]);
    const [newMeal, setNewMeal] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSpinning, setIsSpinning] = useState(false);
    const [weeklyPlan, setWeeklyPlan] = useState([]);

    const fetchMeals = async () => {
        setIsLoading(true);
        try {
            const response = await frugalServicesClient.getMealOptions();
            if (response.status === 401) {
                setPasswordExpired(true);
                return;
            }
            const data = await response.json();
            setMeals(data);
        } catch (error) {
            console.error("Failed to fetch meals:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if(user) {
            fetchMeals();
        }
    }, [user]);

    const handleAddMeal = async () => {
        if (newMeal.trim() === '') return;
        try {
            const response = await frugalServicesClient.createMealOption({
                accountId: user.accountId,
                meal: newMeal,
            });
            if (response.status === 401) {
                setPasswordExpired(true);
                return;
            }
            setNewMeal('');
            fetchMeals(); // Refetch meals to include the new one
        } catch (error) {
            console.error("Failed to add meal:", error);
        }
    };

    const handleDeleteMeal = async (id) => {
        try {
            const response = await frugalServicesClient.deleteMealOption(id);
            if (response.status === 401) {
                setPasswordExpired(true);
                return;
            }
            fetchMeals(); // Refetch meals
        } catch (error) {
            console.error("Failed to delete meal:", error);
        }
    };

    const handleSpin = () => {
        if (meals.length === 0) return;
        setIsSpinning(true);
        setWeeklyPlan([]);

        setTimeout(() => {
            const plan = [];
            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

            if (meals.length < 7) {
                // First, use all unique meals
                const shuffledMeals = [...meals].sort(() => 0.5 - Math.random());
                shuffledMeals.forEach((meal, index) => {
                    plan.push({ day: days[index], meal: meal });
                });

                // Then, fill the remaining days with random picks
                const remainingDays = 7 - meals.length;
                for (let i = 0; i < remainingDays; i++) {
                    const randomIndex = Math.floor(Math.random() * meals.length);
                    plan.push({ day: days[meals.length + i], meal: meals[randomIndex] });
                }
            } else {
                // Shuffle the array to get unique meals
                const shuffledMeals = [...meals].sort(() => 0.5 - Math.random());
                for (let i = 0; i < 7; i++) {
                    plan.push({ day: days[i], meal: shuffledMeals[i] });
                }
            }

            setWeeklyPlan(plan);
            setIsSpinning(false);
        }, 1500);
    };

    const pieColors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9CBCF'];

    return (
        <Box sx={{ display: 'flex', paddingTop: '64px' }}>
            <AppDrawer />
            <Paper sx={{ p: 3, m: 2, width: '100%' }}>
                <Typography variant="h4" gutterBottom>
                    Meal Wheel
                </Typography>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6">Your Meals</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                            <TextField
                                label="New Meal"
                                variant="outlined"
                                value={newMeal}
                                onChange={(e) => setNewMeal(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddMeal()}
                                fullWidth
                            />
                            <Fab
                                color="primary"
                                size="small"
                                onClick={handleAddMeal}
                                sx={{ ml: 1, width: 40, height: 40, minWidth: 40 }}
                            >
                                <AddIcon />
                            </Fab>
                        </Box>
                        {isLoading ? (
                            <CircularProgress />
                        ) : (
                            <List>
                                {meals.map((meal) => (
                                    <ListItem
                                        key={meal.id}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteMeal(meal.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={meal.meal} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }} sx={{ textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={handleSpin}
                            disabled={isSpinning || meals.length === 0}
                        >
                            Spin the Wheel!
                        </Button>

                        {isSpinning && (
                            <Box
                                sx={{
                                    width: 200,
                                    height: 200,
                                    borderRadius: '50%',
                                    background: `conic-gradient(${pieColors.join(', ')})`,
                                    animation: `${spinAnimation} 1.5s linear infinite`,
                                    margin: 'auto',
                                    mt: 4,
                                }}
                            />
                        )}

                        {weeklyPlan.length > 0 && (
                            <Grid container spacing={2} sx={{ mt: 4 }}>
                                {weeklyPlan.map((dayPlan, index) => (
                                    <Grow in={true} key={dayPlan.day} style={{ transformOrigin: '0 0 0' }} {...{ timeout: 500 * (index + 1) }}>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Paper elevation={3} sx={{ p: 2 }}>
                                                <Typography variant="h6" color="primary">{dayPlan.day}</Typography>
                                                <Typography>{dayPlan.meal.meal}</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grow>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default MealWheel;
